import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plan2PlateDrawer, Plan2PlateDrawerHeader } from "./Plan2PlateDrawer";
import { SectionLabel } from "./ui/SectionLabel";
import { Slider } from "./ui/Slider";
import { Input, NumberInput } from "./ui/Input";
import { Button } from "./ui/Button";
import { Spinner } from "./ui/Spinner";
import {
  emptyNutriSearchFilter,
  MealIndicator,
  mealIndicatorOptions,
  NutriSearchFilter,
} from "../data/search";
import { DISH_TAG_LABELS, DishTag } from "../data/dishModification";
import { useMaxNutriValues } from "./nutriSlider";
import { cn } from "../lib/utils";

type Tone = "blue" | "fat" | "carbs" | "protein";

const allTags = Object.keys(DISH_TAG_LABELS) as DishTag[];

export interface NutriFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  indicators: MealIndicator[];
  tags: DishTag[];
  value: NutriSearchFilter;
  personId?: number;
  onApply: (
    indicators: MealIndicator[],
    tags: DishTag[],
    filter: NutriSearchFilter,
  ) => void;
  countResults: (
    indicators: MealIndicator[],
    tags: DishTag[],
    filter: NutriSearchFilter,
  ) => Promise<number>;
}

/**
 * Fullsize-Drawer zum Einstellen der Suche: Art des Essens, Tags und
 * Nährwert-Grenzen. Die Ergebnisanzahl wird entprellt geladen (erst wenn der
 * Schieber nicht mehr bewegt wird) und im Absende-Button als Spinner/Text
 * angezeigt.
 */
export function NutriFilterDrawer(props: NutriFilterDrawerProps) {
  const {
    open,
    onClose,
    indicators,
    tags,
    value,
    personId,
    onApply,
    countResults,
  } = props;
  const [draftIndicators, setDraftIndicators] =
    useState<MealIndicator[]>(indicators);
  const [draftTags, setDraftTags] = useState<DishTag[]>(tags);
  const [draft, setDraft] = useState<NutriSearchFilter>(value);
  const [tagSearch, setTagSearch] = useState("");
  const [count, setCount] = useState<number | undefined>();
  const [counting, setCounting] = useState(false);
  const requestId = useRef(0);

  const maxValues = useMaxNutriValues(draftIndicators, personId, open);

  // Tags sind nur für Gerichte (oder „Alle") relevant.
  const tagsRelevant =
    draftIndicators.length === 0 || draftIndicators.includes("DISH");
  const effectiveTags = tagsRelevant ? draftTags : [];
  const indicatorKey = [...draftIndicators].sort().join(",");

  useEffect(() => {
    if (open) {
      setDraftIndicators(indicators);
      setDraftTags(tags);
      setDraft(value);
      setTagSearch("");
      setCount(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refreshCount = useCallback(
    (
      currentIndicators: MealIndicator[],
      currentTags: DishTag[],
      filter: NutriSearchFilter,
    ) => {
      const id = ++requestId.current;
      setCounting(true);
      countResults(currentIndicators, currentTags, filter)
        .then((resultCount) => {
          if (id === requestId.current) setCount(resultCount);
        })
        .catch(() => {
          if (id === requestId.current) setCount(undefined);
        })
        .finally(() => {
          if (id === requestId.current) setCounting(false);
        });
    },
    [countResults],
  );

  useEffect(() => {
    if (!open || !maxValues) return;
    const handle = setTimeout(
      () => refreshCount(draftIndicators, effectiveTags, draft),
      400,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, maxValues, indicatorKey, draftTags, draft, refreshCount]);

  const filteredTags = useMemo(() => {
    const query = tagSearch.trim().toLowerCase();
    return allTags.filter((tag) =>
      DISH_TAG_LABELS[tag].toLowerCase().includes(query),
    );
  }, [tagSearch]);

  const selectIndicator = (next: MealIndicator | undefined) => {
    if (next === undefined) {
      setDraftIndicators([]);
      return;
    }
    setDraftIndicators((prev) =>
      prev.includes(next)
        ? prev.filter((indicator) => indicator !== next)
        : [...prev, next],
    );
  };

  const toggleTag = (tag: DishTag) => {
    setDraftTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const reset = () => {
    setDraftIndicators([]);
    setDraftTags([]);
    setDraft(emptyNutriSearchFilter);
  };

  const apply = () => {
    onApply(draftIndicators, effectiveTags, draft);
    onClose();
  };

  return (
    <Plan2PlateDrawer
      open={open}
      onClose={onClose}
      header={<Plan2PlateDrawerHeader heading="Suche filtern" />}
      body={
        <div className="flex flex-col gap-5">
          <div>
            <SectionLabel>Art des Essens</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {mealIndicatorOptions.map((option) => {
                const active =
                  option.value === undefined
                    ? draftIndicators.length === 0
                    : draftIndicators.includes(option.value);
                return (
                  <button
                    key={option.label}
                    type="button"
                    data-cy="search-indicator-filter"
                    aria-pressed={active}
                    onClick={() => selectIndicator(option.value)}
                    className={cn(
                      "shrink-0 rounded-pill px-3 py-1.5 text-xs font-bold transition",
                      active
                        ? "bg-gold text-white border border-gold"
                        : "border border-line bg-surface-card text-ink-muted hover:bg-surface",
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {tagsRelevant && (
            <div>
              <SectionLabel>Tags</SectionLabel>
              <Input
                type="search"
                value={tagSearch}
                data-cy="tag-filter-search"
                placeholder="Tags suchen"
                onChange={(e) => setTagSearch(e.target.value)}
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {filteredTags.map((tag) => {
                  const active = draftTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      data-cy="tag-filter-option"
                      aria-pressed={active}
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        "shrink-0 rounded-pill px-3 py-1.5 text-xs font-bold transition",
                        active
                          ? "bg-gold text-white border border-gold"
                          : "border border-line bg-surface-card text-ink-muted hover:bg-surface",
                      )}
                    >
                      {DISH_TAG_LABELS[tag]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <SectionLabel>Nährwerte</SectionLabel>
            {maxValues ? (
              <div className="flex flex-col gap-3">
                <FilterRow
                  label="Kalorien"
                  unit="kcal"
                  tone="blue"
                  maxValue={maxValues.kcal}
                  min={draft.minKcal}
                  max={draft.maxKcal}
                  onMinChange={(v) => setDraft((d) => ({ ...d, minKcal: v }))}
                  onMaxChange={(v) => setDraft((d) => ({ ...d, maxKcal: v }))}
                />
                <FilterRow
                  label="Fett"
                  unit="g"
                  tone="fat"
                  maxValue={maxValues.fat}
                  min={draft.minFat}
                  max={draft.maxFat}
                  onMinChange={(v) => setDraft((d) => ({ ...d, minFat: v }))}
                  onMaxChange={(v) => setDraft((d) => ({ ...d, maxFat: v }))}
                />
                <FilterRow
                  label="KH"
                  unit="g"
                  tone="carbs"
                  maxValue={maxValues.carbs}
                  min={draft.minCarbs}
                  max={draft.maxCarbs}
                  onMinChange={(v) => setDraft((d) => ({ ...d, minCarbs: v }))}
                  onMaxChange={(v) => setDraft((d) => ({ ...d, maxCarbs: v }))}
                />
                <FilterRow
                  label="Protein"
                  unit="g"
                  tone="protein"
                  maxValue={maxValues.protein}
                  min={draft.minProtein}
                  max={draft.maxProtein}
                  onMinChange={(v) =>
                    setDraft((d) => ({ ...d, minProtein: v }))
                  }
                  onMaxChange={(v) =>
                    setDraft((d) => ({ ...d, maxProtein: v }))
                  }
                />
              </div>
            ) : (
              <div className="flex justify-center py-8">
                <Spinner className="text-teal" />
              </div>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center gap-3">
          <Button variant="tertiary" onClick={reset}>
            Zurücksetzen
          </Button>
          <Button
            className="flex-1"
            dataCy="nutri-filter-apply"
            disabled={!maxValues || counting}
            onClick={apply}
          >
            {counting ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <span>
                {count != null
                  ? `${count} Ergebnisse anzeigen`
                  : "Ergebnisse anzeigen"}
              </span>
            )}
          </Button>
        </div>
      }
    />
  );
}

function FilterRow(props: {
  label: string;
  unit: string;
  tone: Tone;
  maxValue: number;
  min: number;
  max: number | undefined;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number | undefined) => void;
}) {
  const { label, unit, tone, maxValue } = props;
  const max = props.max ?? maxValue;
  const clamp = (value: number) => Math.max(0, Math.min(value, maxValue));
  const applyMin = (value: number) => props.onMinChange(clamp(value));
  const applyMax = (value: number | undefined) => {
    if (value == null) {
      props.onMaxChange(undefined);
      return;
    }
    const clamped = clamp(value);
    props.onMaxChange(clamped >= maxValue ? undefined : clamped);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="w-14 shrink-0 text-xs font-semibold text-ink">
          {label}
        </span>
        <Slider
          min={0}
          max={maxValue}
          value={[props.min, max]}
          tone={tone}
          thumbLabels={[`${label} Minimum`, `${label} Maximum`]}
          onValueChange={([minValue, maxValueChange]) => {
            applyMin(minValue);
            applyMax(maxValueChange);
          }}
          className="flex-1"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="w-14 shrink-0 text-xs text-ink-muted">{unit}</span>
        <div className="flex flex-1 items-center justify-between">
          <NumberInput
            value={props.min}
            onValueChange={(v) => applyMin(v ?? 0)}
            aria-label={`${label} Minimum`}
            className="w-20 text-center"
          />
          <NumberInput
            value={max}
            onValueChange={applyMax}
            aria-label={`${label} Maximum`}
            className="w-20 text-center"
          />
        </div>
      </div>
    </div>
  );
}
